import React, { useState } from "react"
// eslint-disable-next-line no-restricted-imports
import { useBreakpointComponent } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import useCreateThread from "./hooks/useCreateThread"
import { InAppMessagingThread } from "./inAppMessagingTypes"
import { useInAppMessaging } from "../context/messaging/InAppMessagingContext"
import useGetThreads from "./hooks/useGetThreads"
import { reportErrorMessage } from "../../utils/ErrorReporting"
import { useCurrentUser } from "../context/user/UserContext"
import { FormattedMessage, useIntl } from "react-intl"
import { Form, FormTextField } from "@carrotfertility/carotene-core-x"
import { Box, Typography, Button, Stack, useTheme, Alert, Link } from "@carrotfertility/carotene-core"
import { FormTextFieldWithCharacterCount } from "#/services/common-forms/FormTextFieldWithCharacterCount"
import { FormCancelButton, FormSendNewMessageButton } from "./InAppMessagingFormButtons"
import { EmployeeSupportUrl } from "../../utils/EmployeeSupportLink"

type MessageButtonContainerProps = {
  onClick: () => void
  disabled: boolean
}

type InAppMessagingCreateNewThreadFormProps = {
  newestThread: InAppMessagingThread
  scrollThreadsListToTop: () => void
}

/**
 * `New message`, at the foot of the conversation list.
 *
 * IN FLOW, below the list, and that is a change from what this was — a 172px block that pulled itself
 * back up over the end of the list (`position: relative; inset-block-end: 172px`) under a
 * `pointer-events: none` gradient, with pointer events re-enabled on a nested box and again inline on
 * the button. That arrangement only works on a page that scrolls: the 172px it occupies has to fall
 * below the fold, or it leaves a band of empty column under the button. The list is bounded to the
 * viewport now (see `messages-shell.tsx`), so the band would be on screen.
 *
 * NO RULE ABOVE IT. A footer divider here was one of the three things reported wrong about the
 * CONAPP-183 restyle; the button sits on the list's own paper with space around it instead.
 *
 * `flexShrink: 0` keeps it at full height while the list above takes the slack, so it cannot be
 * squeezed away on a short viewport — and, unlike the gradient, it never overlaps the last row.
 *
 * NO RADIUS OF ITS OWN, though it paints paper into the column's rounded bottom corners. The column
 * clips: `overflow-y: hidden` makes `overflow-x` compute to `auto`, which makes it a scroll container,
 * and a scroll container clips its descendants to its own border curve. Both halves measured in Chrome
 * — a control box at `overflow: visible` squares the corners off, this one does not.
 */
export function NewMessageButtonContainer({ onClick, disabled }: MessageButtonContainerProps): JSX.Element {
  return (
    <Box
      sx={(theme) => ({
        flexShrink: 0,
        display: "flex",
        justifyContent: "center",
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(theme.tokens.spacing.lg)
      })}
    >
      <Button disabled={disabled} onClick={onClick}>
        <FormattedMessage defaultMessage="New message" />
      </Button>
    </Box>
  )
}

export function InAppMessagingCreateNewThreadForm({
  newestThread,
  scrollThreadsListToTop
}: InAppMessagingCreateNewThreadFormProps): JSX.Element {
  const { mutateAsync } = useCreateThread()
  const { refetch: refetchThreads } = useGetThreads()
  const { setSelectedThread, setShowNewThreadForm } = useInAppMessaging()
  const [isError, setError] = useState(false)
  const { employeeId } = useCurrentUser()
  const intl = useIntl()
  const theme = useTheme()

  const newThreadHeader = useBreakpointComponent([
    false,
    false,
    false,
    <Box key="large">
      <Typography variant="h3" component="h2" color={theme.palette.text.primary}>
        <FormattedMessage defaultMessage="Send a new message" />
      </Typography>
    </Box>
  ])

  async function onSubmit(values: { subject: string; message: string }): Promise<void> {
    if (values?.subject && values?.message) {
      try {
        const data = await mutateAsync(values)
        await refetchThreads()
        setSelectedThread(data)
        setShowNewThreadForm(false)
        scrollThreadsListToTop()
      } catch (error) {
        reportErrorMessage(`Unable to create new thread for employee id: ${employeeId}. Error: ${error}`)
        setError(true)
      }
    }
  }

  return (
    <Form onSubmit={onSubmit}>
      <Stack
        direction="column"
        justifyContent="center"
        spacing={theme.tokens.spacing.xl}
        paddingLeft={(theme) => theme.spacing(theme.tokens.spacing.xxl)}
        paddingRight={(theme) => theme.spacing(theme.tokens.spacing.md)}
      >
        {newThreadHeader}
        <FormTextField
          name="subject"
          aria-label={intl.formatMessage({ defaultMessage: "Subject" })}
          label={intl.formatMessage({ defaultMessage: "Subject" })}
          inputProps={{ maxLength: 150 }}
          required
        />
        <FormTextFieldWithCharacterCount
          name="message"
          multiline
          rows={11}
          placeholder={intl.formatMessage({
            defaultMessage: "Write a message. We'll respond within one business day."
          })}
          maxLength={1600}
          required
          label={intl.formatMessage({ defaultMessage: "Message" })}
          aria-label={intl.formatMessage({ defaultMessage: "Message" })}
        />
        {isError && (
          <Alert severity="error">
            <Typography color={(theme) => theme.palette.error.main}>
              <FormattedMessage
                defaultMessage="Your message failed to send. Please try again or <link>contact us</link>."
                values={{
                  link: (linkContent) => (
                    <Link color="inherit" href={EmployeeSupportUrl} target={"_blank"}>
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </Typography>
          </Alert>
        )}
        <Stack
          direction="row"
          spacing={theme.tokens.spacing.sm}
          justifyContent="flex-end"
          marginBlockEnd={theme.spacing(theme.tokens.spacing.lg)}
        >
          <FormCancelButton
            onClick={() => {
              setShowNewThreadForm(false)
              setSelectedThread(newestThread)
            }}
          />
          <FormSendNewMessageButton />
        </Stack>
      </Stack>
    </Form>
  )
}
